import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import { getMothersByPartner, addMother as addMotherAPI } from '../../components/api';

// Async thunks
export const fetchMothersByPartner = createAsyncThunk(
  'mothers/fetchMothersByPartner',
  async (partnerId, { rejectWithValue }) => {
    try {
      console.log('Fetching mothers for partner:', partnerId);
      const response = await getMothersByPartner(partnerId);
      console.log('API Response:', response);
      
      // Handle different response formats
      let processedResponse = response;
      
      // If response is an array, wrap it in expected format
      if (Array.isArray(response)) {
        processedResponse = {
          partner_id: partnerId,
          partner_name: 'Partner Organization',
          mother_count: response.length,
          mothers: response
        };
      }
      
      // If response has data property, use that
      if (response.data && !response.mothers) {
        processedResponse = {
          partner_id: partnerId,
          partner_name: response.partner_name || 'Partner Organization',
          mother_count: Array.isArray(response.data) ? response.data.length : 0,
          mothers: Array.isArray(response.data) ? response.data : []
        };
      }
      
      // If response is null or undefined, create empty response
      if (!response || (typeof response === 'object' && Object.keys(response).length === 0)) {
        processedResponse = {
          partner_id: partnerId,
          partner_name: 'Partner Organization',
          mother_count: 0,
          mothers: []
        };
      }
      
      // Ensure mothers is always an array
      if (!processedResponse.mothers) {
        processedResponse.mothers = [];
      }
      
      // Ensure mother_count is set
      if (!processedResponse.mother_count) {
        processedResponse.mother_count = processedResponse.mothers.length;
      }
      
      // Validate each mother object has required fields
      processedResponse.mothers = processedResponse.mothers.map((mother, index) => ({
        generated_id: mother.generated_id || mother.id || `MTH${String(index + 1).padStart(3, '0')}`,
        first_name: mother.first_name || mother.firstName || `Mother${index + 1}`,
        surname: mother.surname || mother.lastName || `Surname${index + 1}`,
        mobile_number: mother.mobile_number || mother.phone || mother.phoneNumber || `+256 123 ${String(index + 1).padStart(3, '0')}`,
        location: mother.location || 'Unknown',
        num_children: mother.num_children || mother.childrenCount || 1,
        ages_of_children: mother.ages_of_children || mother.childrenAges || [5],
        savings: mother.savings || 0,
        total_monthly_savings: mother.total_monthly_savings || mother.monthlySavings || 0,
        milestone_score: mother.milestone_score || mother.milestoneScore || 0,
        compliance_score: mother.compliance_score || mother.complianceScore || '0/10',
        education_level: mother.education_level || mother.educationLevel || 'Primary',
        nin: mother.nin || mother.nationalId || `NIN${String(index + 1).padStart(6, '0')}`,
        created_at: mother.created_at || mother.createdAt || new Date().toISOString(),
        donor_profile: mother.donor_profile || mother.donorProfile || null,
        donor_contributions: mother.donor_contributions || mother.donorContributions || 0,
        activities: mother.activities || []
      }));
      
      console.log('Processed response:', processedResponse);
      return processedResponse;
    } catch (error) {
      console.error('Error fetching mothers by partner:', error);
      
      // Fallback to sample data if API is unavailable
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        console.log('API unavailable, using fallback data');
        return {
          partner_id: partnerId,
          partner_name: 'Sample Partner Organization (Offline Mode)',
          mother_count: 20,
          mothers: Array.from({ length: 20 }, (_, i) => ({
            generated_id: `MTH${String(i + 1).padStart(3, '0')}`,
            first_name: `Mother${i + 1}`,
            surname: `Surname${i + 1}`,
            mobile_number: `+256 123 ${String(i + 1).padStart(3, '0')} ${String(i + 1).padStart(3, '0')}`,
            location: ['Kampala', 'Jinja', 'Mbarara', 'Gulu'][i % 4],
            num_children: Math.floor(Math.random() * 4) + 1,
            ages_of_children: Array.from({ length: Math.floor(Math.random() * 4) + 1 }, () => Math.floor(Math.random() * 18) + 1),
            savings: Math.floor(Math.random() * 500000) + 50000,
            total_monthly_savings: Math.floor(Math.random() * 50000) + 10000,
            milestone_score: Math.floor(Math.random() * 100) + 1,
            compliance_score: `${Math.floor(Math.random() * 8) + 2}/10`,
            education_level: ['Primary', 'Secondary', 'Tertiary'][i % 3],
            nin: `NIN${String(i + 1).padStart(6, '0')}`,
            created_at: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
            donor_profile: i % 3 === 0 ? {
              first_name: `Donor${i + 1}`,
              surname: `DonorSurname${i + 1}`,
              email: `donor${i + 1}@example.com`,
              country_of_residence: ['USA', 'UK', 'Canada', 'Australia'][i % 4],
              total_contributions: Math.floor(Math.random() * 1000000) + 100000
            } : null,
            donor_contributions: i % 3 === 0 ? Math.floor(Math.random() * 100000) + 10000 : 0,
            activities: Array.from({ length: Math.floor(Math.random() * 5) + 1 }, (_, j) => ({
              activity_id: `ACT${String(i + 1).padStart(3, '0')}${String(j + 1).padStart(2, '0')}`,
              name: ['Tree Planting', 'Solar Installation', 'Water Conservation', 'Organic Farming', 'Waste Management'][j % 5],
              description: `Activity description for ${['Tree Planting', 'Solar Installation', 'Water Conservation', 'Organic Farming', 'Waste Management'][j % 5]}`,
              num_people: Math.floor(Math.random() * 20) + 5,
              created_at: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
            }))
          }))
        };
      }
      
      return rejectWithValue(error.message);
    }
  }
);

export const updateMotherData = createAsyncThunk(
  'mothers/updateMotherData',
  async ({ motherId, updateData }, { rejectWithValue }) => {
    try {
      console.log('Updating mother:', motherId, updateData);
      // Mock update since there's no corresponding API function
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            motherId,
            ...updateData,
            updated_at: new Date().toISOString()
          });
        }, 200);
      });
      console.log('Update mother response:', response);
      return response;
    } catch (error) {
      console.error('Error updating mother:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const addMother = createAsyncThunk(
  'mothers/addMother',
  async (motherData, { rejectWithValue }) => {
    try {
      console.log('Adding mother:', motherData);
      const response = await addMotherAPI(motherData);
      console.log('Add mother response:', response);
      
      // Handle different response formats
      let processedResponse = response;
      
      // If response has data property, use that
      if (response.data && !response.generated_id) {
        processedResponse = {
          ...motherData,
          ...response.data,
          generated_id: response.data.id || response.data.generated_id || `MTH${Date.now()}`,
          created_at: response.data.created_at || new Date().toISOString()
        };
      }
      
      // Ensure required fields are present
      if (!processedResponse.generated_id) {
        processedResponse.generated_id = response.id || `MTH${Date.now()}`;
      }
      
      if (!processedResponse.created_at) {
        processedResponse.created_at = new Date().toISOString();
      }
      
      console.log('Processed add mother response:', processedResponse);
      return processedResponse;
    } catch (error) {
      console.error('Error adding mother:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  mothers: [],
  partnerInfo: null,
  isLoading: false,
  error: null,
  filters: {
    location: '',
    children: '',
    savingsRange: [0, 1000000],
    climateFilter: false,
  },
  pagination: {
    page: 0,
    rowsPerPage: 10,
    totalCount: 0,
  },
  selectedMothers: [],
  lastUpdated: null,
};

const mothersSlice = createSlice({
  name: 'mothers',
  initialState,
  reducers: {
    clearMothersError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 0; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 0;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setSelectedMothers: (state, action) => {
      state.selectedMothers = action.payload;
    },
    toggleMotherSelection: (state, action) => {
      const motherId = action.payload;
      const index = state.selectedMothers.indexOf(motherId);
      if (index > -1) {
        state.selectedMothers.splice(index, 1);
      } else {
        state.selectedMothers.push(motherId);
      }
    },
    clearSelectedMothers: (state) => {
      state.selectedMothers = [];
    },
    updateMotherInList: (state, action) => {
      const { motherId, updateData } = action.payload;
      const index = state.mothers.findIndex(mother => mother.generated_id === motherId);
      if (index !== -1) {
        state.mothers[index] = { ...state.mothers[index], ...updateData };
      }
    },
    addMotherToList: (state, action) => {
      state.mothers.unshift(action.payload);
      state.pagination.totalCount += 1;
    },
    removeMotherFromList: (state, action) => {
      const motherId = action.payload;
      state.mothers = state.mothers.filter(mother => mother.generated_id !== motherId);
      state.pagination.totalCount = Math.max(0, state.pagination.totalCount - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Mothers
      .addCase(fetchMothersByPartner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMothersByPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mothers = action.payload.mothers || [];
        state.partnerInfo = {
          partner_id: action.payload.partner_id,
          partner_name: action.payload.partner_name,
          mother_count: action.payload.mother_count
        };
        state.pagination.totalCount = action.payload.mother_count;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchMothersByPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Mother
      .addCase(updateMotherData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateMotherData.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.mothers.findIndex(mother => mother.generated_id === action.payload.motherId);
        if (index !== -1) {
          state.mothers[index] = { ...state.mothers[index], ...action.payload };
        }
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(updateMotherData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Mother
      .addCase(addMother.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addMother.fulfilled, (state, action) => {
        state.isLoading = false;
        state.mothers.unshift(action.payload);
        state.pagination.totalCount += 1;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(addMother.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearMothersError,
  setFilters,
  clearFilters,
  setPagination,
  setSelectedMothers,
  toggleMotherSelection,
  clearSelectedMothers,
  updateMotherInList,
  addMotherToList,
  removeMotherFromList,
} = mothersSlice.actions;

// Basic selectors
export const selectMothers = (state) => state.mothers;
export const selectMothersList = (state) => state.mothers.mothers;
export const selectPartnerInfo = (state) => state.mothers.partnerInfo;
export const selectMothersLoading = (state) => state.mothers.isLoading;
export const selectMothersError = (state) => state.mothers.error;
export const selectMothersFilters = (state) => state.mothers.filters;
export const selectMothersPagination = (state) => state.mothers.pagination;
export const selectSelectedMothers = (state) => state.mothers.selectedMothers;
export const selectMotherCount = (state) => state.mothers.partnerInfo?.mother_count || 0;

// Memoized climate keywords for better performance
const CLIMATE_KEYWORDS = new Set([
  'climate', 'tree', 'green', 'environment', 'solar', 'eco', 'recycle', 'sustain', 
  'water', 'clean', 'energy', 'carbon', 'forest', 'agro', 'garden', 'compost', 
  'biodiversity', 'organic', 'nature', 'earth', 'renewable', 'conservation', 
  'farming', 'plant', 'waste', 'climate-smart', 'climate change', 'resilience', 
  'adaptation', 'mitigation', 'agroforestry'
]);

// Optimized filtered mothers selector with memoization
export const selectFilteredMothers = createSelector(
  [selectMothersList, selectMothersFilters],
  (mothers, filters) => {
    if (!mothers || mothers.length === 0) return [];

    let filtered = mothers;

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(m => m.location === filters.location);
    }

    // Children filter
    if (filters.children) {
      filtered = filtered.filter(m => String(m.num_children) === String(filters.children));
    }

    // Savings range filter
    if (filters.savingsRange[0] !== 0 || filters.savingsRange[1] !== 1000000) {
      filtered = filtered.filter(m => 
        m.savings >= filters.savingsRange[0] && m.savings <= filters.savingsRange[1]
      );
    }

    // Climate filter - optimized with Set for O(1) lookup
    if (filters.climateFilter) {
      filtered = filtered.filter(m => {
        if (!m.activities || m.activities.length === 0) return false;
        
        return m.activities.some(activity => {
          const activityText = `${activity.name} ${activity.description}`.toLowerCase();
          return Array.from(CLIMATE_KEYWORDS).some(keyword => 
            activityText.includes(keyword)
          );
        });
      });
    }

    return filtered;
  }
);

// Memoized paginated mothers selector
export const selectPaginatedMothers = createSelector(
  [selectFilteredMothers, selectMothersPagination],
  (filteredMothers, pagination) => {
    const start = pagination.page * pagination.rowsPerPage;
    return filteredMothers.slice(start, start + pagination.rowsPerPage);
  }
);

// Memoized filter options selectors
export const selectLocationOptions = createSelector(
  [selectMothersList],
  (mothers) => {
    if (!mothers || mothers.length === 0) return [];
    const locations = new Set(mothers.map(m => m.location).filter(Boolean));
    return Array.from(locations);
  }
);

export const selectChildrenOptions = createSelector(
  [selectMothersList],
  (mothers) => {
    if (!mothers || mothers.length === 0) return [];
    const children = new Set(mothers.map(m => m.num_children).filter(n => n !== undefined && n !== null));
    return Array.from(children).sort((a, b) => a - b);
  }
);

export default mothersSlice.reducer;
